import {BaseBadge, BaseButton, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useFreezeWallet, useUnfreezeWallet, useWallets, useWalletStats} from "@/hooks/useWallets";
import {StatsCard} from "@/pages/Dashboard/Home/components/StatsCard";
import type {Wallet} from "@/services/wallets.service";
import {formatCurrency} from "@/utils/format";
import {CheckCircle, DollarSign, LockOpen, Snowflake, Wallet as WalletIcon} from "lucide-react";
import {useState} from "react";

function statusVariant(status: string): "success" | "warning" | "danger" | "neutral" {
  const map: Record<string, "success" | "warning" | "danger" | "neutral"> = {
    active: "success",
    frozen: "warning",
    closed: "danger",
  };
  return map[status] ?? "neutral";
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {active: "Activo", frozen: "Congelado", closed: "Cerrado"};
  return map[status] ?? status;
}

export function Wallets() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const filters = {
    page,
    limit,
    ...(search ? {search} : {}),
    ...(statusFilter ? {status: statusFilter} : {}),
  };

  const {data, isLoading} = useWallets(filters);
  const {data: stats} = useWalletStats();
  const freezeWallet = useFreezeWallet();
  const unfreezeWallet = useUnfreezeWallet();

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<Wallet>[] = [
    {
      key: "userEmail",
      header: "Usuario",
      render: row => (
        <div>
          <p className="text-sm font-medium text-(--color-text-primary)">{row.userEmail}</p>
          <p className="text-xs text-(--color-text-tertiary) font-mono">{row?.userId?.slice(0, 8)}…</p>
        </div>
      ),
    },
    {
      key: "currency",
      header: "Moneda",
      render: row => <span className="text-sm font-mono text-(--color-text-primary)">{row.currency}</span>,
    },
    {
      key: "balance",
      header: "Saldo",
      render: row => (
        <span className="text-sm font-semibold tabular-nums text-(--color-text-primary)">
          {formatCurrency(row.balance, row.currency)}
        </span>
      ),
    },
    {
      key: "availableBalance",
      header: "Disponible",
      render: row => (
        <span className="text-sm tabular-nums text-(--color-text-secondary)">
          {formatCurrency(row.availableBalance, row.currency)}
        </span>
      ),
    },
    {
      key: "frozenBalance",
      header: "Congelado",
      render: row => (
        <span className="text-sm tabular-nums text-(--color-text-tertiary)">
          {row.frozenBalance > 0 ? formatCurrency(row.frozenBalance, row.currency) : "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={statusVariant(row.status)}
          label={statusLabel(row.status)}
          dot
        />
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          {row.status === "active" && (
            <BaseButton
              variant="ghost"
              size="sm"
              onClick={() => freezeWallet.mutate(row.id)}
              title="Congelar"
            >
              <Snowflake size={14} />
            </BaseButton>
          )}
          {row.status === "frozen" && (
            <BaseButton
              variant="ghost"
              size="sm"
              onClick={() => unfreezeWallet.mutate(row.id)}
              title="Descongelar"
            >
              <LockOpen size={14} />
            </BaseButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-(--color-text-primary)">Wallets</h1>
        <p className="text-sm text-(--color-text-secondary) mt-1">{pagination.total} wallets registrados</p>
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatsCard
            label="Total Wallets"
            value={stats.totalWallets.toLocaleString()}
            icon={<WalletIcon size={20} />}
            iconColor="var(--color-primary)"
          />
          <StatsCard
            label="Activos"
            value={stats.activeWallets.toLocaleString()}
            icon={<CheckCircle size={20} />}
            iconColor="var(--color-success)"
          />
          <StatsCard
            label="Congelados"
            value={stats.frozenWallets.toLocaleString()}
            icon={<Snowflake size={20} />}
            iconColor="var(--color-warning)"
          />
          <StatsCard
            label="Balance Total (USD)"
            value={formatCurrency(stats.totalBalanceUsd, "USD")}
            icon={<DollarSign size={20} />}
            iconColor="var(--color-accent)"
          />
        </div>
      )}

      <BaseCard padding="md">
        <div className="flex items-center gap-4">
          <input
            type="text"
            placeholder="Buscar por email o ID…"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-md border border-(--color-border) bg-(--color-surface) text-sm text-(--color-text-primary) w-64"
          />
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-md border border-(--color-border) bg-(--color-surface) text-sm text-(--color-text-primary)"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activo</option>
            <option value="frozen">Congelado</option>
            <option value="closed">Cerrado</option>
          </select>
          {(search || statusFilter) && (
            <BaseButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setStatusFilter("");
                setPage(1);
              }}
            >
              Limpiar
            </BaseButton>
          )}
        </div>
      </BaseCard>

      <BaseCard padding="none">
        <BaseTable<Wallet>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron wallets."
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>
    </div>
  );
}
